import React, { useState, useEffect, useRef } from 'react'
import * as d3 from 'd3'
import { GRAPH_COLOR_PALLET4 } from '../../constants/index'
import Legend from '../legend'
import CustomTootTip1 from './customTootTip1'
import { Wrapper, ToolTipWrapper, CustomAxisX, NoDataFound } from './style'

// The dynamic data
const defaultData1 = [
  // { label: 'A', category: 'category 1', values: { v1: 22, v2: 25, v3: 40 } },
  // { label: 'B', category: 'category 2', values: { v1: 31, v2: 30, v3: 25 } },
  // { label: 'C', category: 'category 3', values: { v1: 42, v2: 38, v3: 39 } },
  // { label: 'D', category: 'category 4', values: { v1: 22, v2: 45, v3: 38 } },
  // { label: 'E', category: 'category 5', values: { v1: 40, v2: 33, v3: 30 } },
  // { label: 'F', category: 'category 6', values: { v1: 28, v2: 27, v3: 39 } },
]

const defaultData2 = [
  // { category: 'A1', label: 'Positive', value: 22 },
  // { category: 'A1', label: 'Mixed', value: 25 },
  // { category: 'A1', label: 'Negative', value: 40 },
  // { category: 'B2', label: 'Positive', value: 31 },
  // { category: 'B2', label: 'Mixed', value: 30 },
  // { category: 'B2', label: 'Negative', value: 25 },
  // { category: 'C3', label: 'Positive', value: 42 },
  // { category: 'C3', label: 'Mixed', value: 38 },
  // { category: 'C3', label: 'Negative', value: 39 },
]

// NOTE: Placeholder stubs for dependencies are omitted for conciseness, 
// assuming they exist in your actual environment.

const convertData = (data) => {
  const result = []
  const labels = Array.from(new Set(data.map((item) => item.label)))

  const grouped = data.reduce((acc, { category, label, value }) => {
    if (!acc[category]) {
      acc[category] = {}
    }
    acc[category][label] = value
    return acc
  }, {})

  for (const category in grouped) {
    const categoryData = grouped[category]
    const values = {}

    labels.forEach((label, index) => {
      values[`v${index + 1}`] = categoryData[label] || 0
      values[`t${index + 1}`] = label
    })

    result.push({
      label: category,
      values: values,
    })
  }
  return result
}


const D3VerticalStacked = ({
  dataFormat = 1,
  data,
  settings,
  insightsElementType,
  showNoDataFound = false,
}) => {
  const [hoverObject, setHoverObject] = useState({})
  const svgRef = useRef()
  const graphData =
    dataFormat === 1
      ? (data || defaultData1).map((d, i) => ({
          ...d,
          id: `id_${i}_${d.label.replaceAll(' ', '')}`,
        }))
      : convertData(data || defaultData2)
  const tooltipRef = useRef()

  const {
    height: svgHeight0 = 500,
    width: svgWidth0 = 800,
    maxValue: maxVal = null,
    yAxisTicks = yAxisTicks || 100,
    margin = { top: 20, right: 20, bottom: 100, left: 60 },
    legend = false,
    color = GRAPH_COLOR_PALLET4,
    toolTip = false,
    scaleX = false,
    scaleY = false,
    scaleLineColor = '#333',
    scaleTextColor = '#333',
    outline = false,
    outlineColor = GRAPH_COLOR_PALLET4,
    outlineWidth = outline ? settings?.outlineWidth || 3 : 0,
    textColor = ['white', 'black'],
    customToolTip = 'none',
    barWidth = 22,
  } = settings || {}

  const loadGraph = () => {
    const svgWidth = getSvgWidth()
    const svgHeight = svgHeight0

    const width = svgWidth - margin.left - margin.right
    const height = svgHeight - margin.top - margin.bottom

    clearSvg()
    const svg = createSvgElement(svgWidth, svgHeight)

    // Ensure we have data to process
    if (graphData.length === 0 || !graphData[0]?.values) return

    const keys = getKeys(graphData[0])
    const stackedData = stackData(graphData, keys)

    const x = createXScale(width)
    const y = createYScale(height)

    renderAxis(svg, x, y, width, height)
    renderGridLines(svg, y, width, height)

    if (stackedData.length > 0) {
      renderStack(svg, stackedData, x, y, width, height)
      if (toolTip) attachTooltips(svg, x, y)
      renderBarLabels(svg, x, y, height)
    }
  }

  const getSvgWidth = () => {
    if (settings.width === '100%') {
      const elementPadding = parseFloat(
        window.getComputedStyle(svgRef.current.parentNode).paddingRight
      )
      const wrapperWidth = Math.round(svgRef.current.parentNode.getBoundingClientRect().width)
      return wrapperWidth - margin.left - margin.right - elementPadding
    }
    return svgWidth0
  }

  const clearSvg = () => {
    d3.select(svgRef.current).select('svg').selectAll('*').remove()
  }

  const createSvgElement = (svgWidth, svgHeight) => {
    return d3
      .select(svgRef.current)
      .select('svg')
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)
  }

  const getKeys = (data) => {
    if (!data || !data.values) return []
    return Object.keys(data.values).filter((f) => f.includes('v'))
  }

  const stackData = (data, keys) => {
    const stack = d3.stack().keys(keys)
    return stack(data.map((d) => d.values))
  }

  const createXScale = (width) => {
    return d3
      .scaleBand()
      .domain(graphData.map((d) => d.label))
      .range([0, width])
      .padding(0.3)
  }

  const createYScale = (height) => {
        // Find the maximum sum of values (total bar height)
        const calculatedMax = d3.max(graphData, (d) => d3.sum(Object.values(d.values))) || 0
        
        // Use maxVal if provided, otherwise ensure the domain max is at least 1 
        // (or 100 if we know it must be percentage-based and scaled up to 100)
        // Since the maxValue setting is 30 in your sample, we stick to 1 as the minimum non-zero value.
        const domainMax = maxVal !== null ? maxVal : Math.max(calculatedMax, 1);

      return d3
        .scaleLinear()
        .domain([0, domainMax])
        .nice()
        .range([height, 0])
  }

  const renderAxis = (svg, x, y, width, height) => {
    if (scaleX) {
      svg
        .append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x))
        .classed('x-axis', true)
      styleAxis(svg, '.x-axis')
    }

    if (scaleY) {
      svg.append('g').call(d3.axisLeft(y).ticks(yAxisTicks)).classed('y-axis', true)
      styleAxis(svg, '.y-axis')
    }
  }

  const styleAxis = (svg, axisClass) => {
    svg.select(axisClass).selectAll('line, path').style('stroke', scaleLineColor)
    svg
      .select(axisClass)
      .selectAll('text')
      .style('fill', scaleTextColor)
      .style('font-family', 'Poppins,Helvetica,Arial,sans-serif')
      .style('font-size', '12px')
      .style('font-weight', '300')
  }

  const renderGridLines = (svg, y, width, height) => {
    const numLines = yAxisTicks
    const actualMax = y.domain()[1];
    
    if (!isFinite(actualMax) || actualMax <= 0) return; 

    const tickValue = actualMax / numLines

    for (let i = 0; i <= numLines; i++) {
      svg
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', y(tickValue * i))
        .attr('y2', y(tickValue * i))
        .attr('stroke', '#EFEFEF')
        .attr('stroke-width', 1)
    }
  }

  const renderStack = (svg, stackedData, x, y, width, height) => {
    svg
      .selectAll('.stack')
      .data(stackedData)
      .enter()
      .append('g')
      .style('fill', outline ? 'transparent' : (d, i) => color[i % color.length])
      .attr('stroke', (d, i) =>
        settings.outline
          ? settings.outlineColor
            ? outlineColor[i % outlineColor.length]
            : color[i % color.length]
          : 'none'
      )
      .style('stroke-width', outlineWidth)
      .selectAll('rect')
      .data((d) => d)
      .enter()
      .append('rect')
      .attr('x', (d, i) =>
        Math.round(x(graphData[i].label) + x.bandwidth() / 2 - barWidth / 2 || 0)
      )
      .attr('y', (d) => {
            // FIX: Explicitly check if y(d[1] || 0) returns NaN. If so, return 0.
            const yValue = y(d[1] || 0)
            return isNaN(yValue) ? 0 : yValue
        })
      .attr('width', barWidth)
      .attr('height', (d) => {
            // Ensure height is not NaN or negative
            const heightValue = y(d[0]) - y(d[1]);
            return Math.max(0, isNaN(heightValue) ? 0 : heightValue);
        })
      .attr('label', (d, i) => graphData[i].label)
      .attr('id', (d, i) => graphData[i].id)
  }

  const attachTooltips = (svg, x, y) => {
    svg
      .selectAll('rect')
      .on('mouseover', function (event, d, i) {
        const idIndex = event.target.id.split('_')
        const graphDataIndex = parseInt(idIndex[1])
        const targetData = graphData[graphDataIndex]

        if (targetData && targetData.label.replaceAll(' ', '') === idIndex[2]) {
          setHoverObject({ id: event.target.id, data: targetData, stackedData: d })
        } else {
             setHoverObject({ id: event.target.id, data: targetData, stackedData: d })
        }


        const textColor = outline ? 'black' : 'white'
        d3.select(tooltipRef.current)
          .style('visibility', 'visible')
          .style('z-index', '100')
          .style('display', 'block')
          .style('color', textColor)
          .style('border-radius', 0)
          .style('box-shadow', '0px 2px 12px 0px #0000002E')
          .style('border', 0)
          .style('opacity', 1)

        positionTooltip(event)
      })
      .on('mousemove', positionTooltip)
      .on('mouseout', () => {
        d3.select(tooltipRef.current)
          .style('visibility', 'hidden')
          .style('opacity', 0)
          .style('display', 'none')
          setHoverObject({})
      })
  }

  const positionTooltip = (event) => {
    if (['toolTip1', 'toolTip2'].includes(customToolTip)) {
      const wt = tooltipRef.current.getBoundingClientRect().width
      const posi = { top: event.offsetY - 30, left: event.offsetX + 30, class: 'tooltipPosiRight' }

      if (tooltipRef.current?.classList?.contains('tooltipPosiLeft')) {
        tooltipRef.current.classList.remove('tooltipPosiLeft')
      }
      if (tooltipRef.current?.classList?.contains('tooltipPosiRight')) {
        tooltipRef.current.classList.remove('tooltipPosiRight')
      }

      if (event.clientX > window.innerWidth - wt - 20) {
        posi.left = event.offsetX - wt - 20
        posi.class = 'tooltipPosiLeft'
        tooltipRef.current.classList.add('tooltipPosiLeft')
      } else {
        tooltipRef.current.classList.add('tooltipPosiRight')
      }
      d3.select(tooltipRef.current).style('top', `${posi.top}px`).style('left', `${posi.left}px`)
    }
  }

  const renderBarLabels = (svg, x, y, height) => {
    svg
      .selectAll('.bar-label')
      .data(graphData)
      .enter()
      .append('text')
      .attr('class', 'bar-label')
      .attr('x', (d) => x(d.label) + x.bandwidth() / 2)
      .attr('y', (d) => {
            const yValue = y(d3.sum(Object.values(d.values))) - 5;
            // Ensure y is not NaN
            return isNaN(yValue) ? 0 : yValue;
        })
      .attr('text-anchor', 'middle')
      .style('fill', 'black')
      .style('font-family', 'Poppins,Helvetica,Arial,sans-serif')
      .style('font-size', (d) => (d3.sum(Object.values(d.values)) === 0 ? '0px' : '12px'))
      .style('font-weight', '300')
      .text((d) => `${d3.sum(Object.values(d.values))}%`)

    svg
      .selectAll('.bar-label-x1')
      .data(graphData)
      .enter()
      .append('text')
      .attr('class', 'bar-label-x1')
      .attr('x', (d) => x(d.label) + x.bandwidth() / 2)
      .attr('y', (d) => height + 15)
      .attr('text-anchor', 'middle')
      .style('fill', '#000c')
      .style('font-family', 'Poppins,Helvetica,Arial,sans-serif')
      .style('font-size', '12px')
      .style('font-weight', '300')
      .style('opacity', (d) => ((showNoDataFound || d?.entryType === 'fakeEntry') ? 0 : 1))
      .text((d) => d.label.split(' ')[0])

    svg
      .selectAll('.bar-label-x2')
      .data(graphData)
      .enter()
      .append('text')
      .attr('class', 'bar-label-x2')
      .attr('x', (d) => x(d.label) + x.bandwidth() / 2)
      .attr('y', (d) => height + 29)
      .attr('text-anchor', 'middle')
      .style('fill', '#000c')
      .style('font-family', 'Poppins,Helvetica,Arial,sans-serif')
      .style('font-size', '12px')
      .style('font-weight', '300')
      .style('opacity', (d) => ((showNoDataFound || d?.entryType === 'fakeEntry') ? 0 : 1))
      .text(
        (d) =>
          `${d.label.split(' ')[1]} ${d.label.split(' ')[2] || ''}` || d.label.split(' ')[1] || ''
      )
  }

  useEffect(() => {
    if (svgRef.current) {
      loadGraph()
      window.addEventListener('resize', loadGraph)
      return () => window.removeEventListener('resize', loadGraph)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, settings])

  return (
    <Wrapper ref={svgRef}>
      <svg></svg>

      <ToolTipWrapper ref={tooltipRef}>
        {customToolTip === 'none' && (
          <div>
            <strong>Description:</strong> {hoverObject.data?.v1}
            <br />
            <strong>Value:</strong> {hoverObject.stackedData ? (hoverObject.stackedData[1] - hoverObject.stackedData[0]).toFixed(1) : ''}%
          </div>
        )}

        {['toolTip1', 'toolTip2'].includes(customToolTip) && (
          <>
            {hoverObject?.id && hoverObject?.data && (
              <CustomTootTip1
                data={hoverObject}
                type={customToolTip}
                insightsElementType={insightsElementType}
              />
            )}
          </>
        )}
      </ToolTipWrapper>

      {showNoDataFound && (
        <NoDataFound className='noDataFound'>
          <b>No Data</b>
          <p>Switch to a broader range to explore available insights</p>
        </NoDataFound>
      )}

      {legend && graphData?.length && (
        <Legend data={graphData.slice(0, 3).map((d) => d.label)} color={color} />
      )}
    </Wrapper>
  )
}

export default D3VerticalStacked